import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  HistoryOutlined,
  ProjectOutlined,
} from '@ant-design/icons';
import { Input, Modal, Select, Space, message, theme } from 'antd';
import { useState } from 'react';
import { rejectBudgetApi } from 'api/hallazgoFlujo';
import { RowActions, ROW_ACTIONS_WIDTH } from 'components/global/DataTable';
import { buildRowActions } from './buildRowActions';
import { BudgetDownloadButton, useBudgetMenuActions } from './BudgetDropdownButton';
import BudgetVersionHistoryDrawer from './BudgetVersionHistoryDrawer';

const { useToken } = theme;

const REJECT_CATEGORIAS = [
  { value: 'precio_alto', label: 'Precio alto' },
  { value: 'no_aprobado_admin', label: 'No aprobado por administración' },
  { value: 'otro_proveedor', label: 'Otro proveedor' },
  { value: 'postergado', label: 'Postergado' },
  { value: 'otro', label: 'Otro' },
];

// Icono de cada ítem del menú "…" (la lista de ítems la decide `buildRowActions`).
const MENU_ICONS = {
  reject: <CloseOutlined />,
  edit: <EditOutlined />,
  openOdtForm: <EditOutlined />,
  openOdtStatus: <EyeOutlined />,
  openGantt: <ProjectOutlined />,
  reversion: <FileAddOutlined />,
  history: <HistoryOutlined />,
  delete: <DeleteOutlined />,
};

// Ancho de la columna de acciones (lo usa BudgetTable). Vive en RowActions.
export const ACTIONS_WIDTH = ROW_ACTIONS_WIDTH;

function BudgetRowActions({
  record,
  onEdit,
  onApprove,
  onOpenOdtForm,
  onOpenOdtStatus,
  onDownloadOdt,
  onOpenGantt,
  refetch,
}) {
  const { token } = useToken();
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCategoria, setRejectCategoria] = useState('otro');
  const [rejectDetalle, setRejectDetalle] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const menuActions = useBudgetMenuActions({
    budget: record,
    refreshData: refetch,
    editBudget: onEdit,
  });

  const { primary, menu } = buildRowActions(record);

  const handleConfirmReject = async () => {
    setLoading(true);
    try {
      const result = await rejectBudgetApi(record.idBudget, {
        categoria: rejectCategoria,
        detalle: rejectDetalle || undefined,
      });
      if (result?.codeMessage) {
        message.error(result.message || 'Error al rechazar el presupuesto');
      } else {
        message.success('Presupuesto rechazado');
        setRejectOpen(false);
        refetch?.();
      }
    } finally {
      setLoading(false);
    }
  };

  const openReject = () => {
    setRejectCategoria('otro');
    setRejectDetalle('');
    setRejectOpen(true);
  };

  // key de acción → qué hace. Una sola tabla para el botón principal y el menú.
  const run = {
    approve: () => onApprove?.(record),
    openOdtForm: () => onOpenOdtForm?.(record),
    openOdtStatus: () => onOpenOdtStatus?.(record),
    downloadOdt: () => onDownloadOdt?.(record),
    openGantt: () => onOpenGantt?.(record),
    reject: openReject,
    history: () => setHistoryOpen(true),
    edit: menuActions.edit,
    reversion: menuActions.reversion,
    delete: menuActions.remove,
  };

  const menuItems = menu.map(({ key, label, danger }) => ({
    key,
    label,
    danger,
    icon: MENU_ICONS[key],
  }));

  return (
    <>
      <RowActions
        primary={
          primary
            ? { label: primary.label, tone: primary.tone, onClick: () => run[primary.key]?.() }
            : null
        }
        icon={<BudgetDownloadButton budget={record} />}
        menu={{ items: menuItems, onClick: ({ key }) => run[key]?.() }}
        menuDisabled={loading}
      />

      <BudgetVersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        idBudget={record.idBudget}
      />

      <Modal
        open={rejectOpen}
        title="Rechazar presupuesto"
        onCancel={() => setRejectOpen(false)}
        onOk={handleConfirmReject}
        okText="Rechazar"
        okButtonProps={{ danger: true, loading }}
        cancelButtonProps={{ disabled: loading }}
        destroyOnHidden
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="middle">
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: token.colorTextSecondary }}>
              Motivo de rechazo
            </div>
            <Select
              style={{ width: '100%' }}
              options={REJECT_CATEGORIAS}
              value={rejectCategoria}
              onChange={setRejectCategoria}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: token.colorTextSecondary }}>
              Detalle (opcional)
            </div>
            <Input.TextArea
              rows={2}
              placeholder="Información adicional..."
              value={rejectDetalle}
              onChange={(e) => setRejectDetalle(e.target.value)}
              maxLength={300}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

export default BudgetRowActions;
